import type {
  AiUsageOperation,
  BuildModel,
  ClaudeMdPrecedence,
  ClaudeMdSuggestionStatus,
  DraftPrHandling,
  GithubConnectionAttemptStatus,
  GithubInstallationSetupStatus,
  InviteStatus,
  LessonSource,
  PathFilterMode,
  RepoRuleDraftStatus,
  CodeGraphStatus,
  RepoScanStatus,
  RepoStack,
  ReviewModel,
  ReviewProfile,
  ReviewRuleAction,
  ReviewRuleField,
  ReviewRuleOperator,
  ReviewRunStatus,
  ReviewTypeDefault,
  RuleLanguage,
  RuleSource,
  RuleStatus,
  SeverityThreshold,
} from '@/types/models'

export const INVITE_STATUS_LABELS: Record<InviteStatus, string> = {
  pending: 'Pending',
  accepted: 'Accepted',
  revoked: 'Revoked',
  expired: 'Expired',
}

export const INVITE_STATUS_CLASSES: Record<InviteStatus, string> = {
  pending: 'text-primary',
  accepted: 'text-on-surface-variant',
  revoked: 'text-error',
  expired: 'text-on-surface-variant',
}

export const GITHUB_INSTALLATION_SETUP_STATUS_LABELS: Record<GithubInstallationSetupStatus, string> = {
  1: 'Pending',
  2: 'Completed',
  3: 'Failed',
}

export const GITHUB_CONNECTION_ATTEMPT_STATUS_LABELS: Record<GithubConnectionAttemptStatus, string> = {
  1: 'Pending',
  2: 'Completed',
  3: 'Failed',
}

export const REVIEW_PROFILE_LABELS: Record<ReviewProfile, string> = {
  1: 'Chill',
  2: 'Assertive',
}

export const REVIEW_TYPE_DEFAULT_LABELS: Record<ReviewTypeDefault, string> = {
  1: 'Comment only',
  2: 'Request changes',
}

export const SEVERITY_THRESHOLD_LABELS: Record<SeverityThreshold, string> = {
  1: 'Nitpick',
  2: 'Suggestion',
  3: 'Warning',
  4: 'Critical',
}

export const REVIEW_MODEL_LABELS: Record<ReviewModel, string> = {
  1: 'Claude Sonnet',
  2: 'Claude Opus',
  3: 'Claude Fable',
  4: 'Claude Haiku',
}

/**
 * Deliberately its own map, not an alias of REVIEW_MODEL_LABELS: the two enums
 * happen to list the same models today but are free to diverge, so a build
 * model must not render through the review model's labels.
 */
export const BUILD_MODEL_LABELS: Record<BuildModel, string> = {
  1: 'Claude Sonnet',
  2: 'Claude Opus',
  3: 'Claude Fable',
}

/**
 * Mirrors App\Enums\BuildModel::DEFAULT. Only ever a placeholder for a project
 * that hasn't loaded yet — the API decides an unset project's model and always
 * sends one back, so nothing here should treat this as the answer.
 */
export const DEFAULT_BUILD_MODEL: BuildModel = 2

/**
 * Mirrors the projects.max_concurrent_builds column default. Only ever a
 * placeholder for a project that hasn't loaded yet, same reasoning as
 * DEFAULT_BUILD_MODEL above.
 */
export const DEFAULT_MAX_CONCURRENT_BUILDS = 1

/**
 * Mirrors config('typhoonhq.qa_fix_retry_limit')'s own default. Unlike
 * DEFAULT_MAX_CONCURRENT_BUILDS above, this never seeds a ref — a project's
 * qa_fix_retry_limit is null unless it's set its own override, and null is
 * itself a valid, renderable "unset" state. This constant exists only to
 * spell out what null resolves to in the field's placeholder text.
 */
export const DEFAULT_QA_FIX_RETRY_LIMIT = 3

export const CLAUDE_MD_PRECEDENCE_LABELS: Record<ClaudeMdPrecedence, string> = {
  1: "Repo's own CLAUDE.md wins",
  2: 'TyphoonHQ default overrides it',
}

/**
 * Mirrors App\Enums\ClaudeMdPrecedence::DEFAULT. Only ever a placeholder for a
 * project that hasn't loaded yet, same reasoning as DEFAULT_BUILD_MODEL above.
 */
export const DEFAULT_CLAUDE_MD_PRECEDENCE: ClaudeMdPrecedence = 1

export const DRAFT_PR_HANDLING_LABELS: Record<DraftPrHandling, string> = {
  1: 'Never review drafts',
  2: 'Always review drafts',
  3: 'Review when marked ready',
}

export const REVIEW_RULE_FIELD_LABELS: Record<ReviewRuleField, string> = {
  1: 'Base branch',
  2: 'Head branch',
  3: 'Author',
  4: 'Draft status',
  5: 'PR files changed',
  6: 'PR lines changed',
}

export const REVIEW_RULE_OPERATOR_LABELS: Record<ReviewRuleOperator, string> = {
  1: 'Equals',
  2: 'Not equals',
  3: 'Contains',
  4: 'Matches',
  5: 'Greater than',
  6: 'Less than',
  7: 'Is true',
  8: 'Is false',
}

export const REVIEW_RULE_ACTION_LABELS: Record<ReviewRuleAction, string> = {
  1: 'Review',
  2: 'Skip',
}

/**
 * Which operators are valid for a given trigger rule field. Mirrors the
 * backend's App\Rules\ReviewRuleFieldOperatorCompatible whitelist so the FE
 * dropdown never offers a combination the API would reject.
 */
export const OPERATORS_BY_FIELD: Record<ReviewRuleField, ReviewRuleOperator[]> = {
  1: [1, 2, 3, 4], // BaseBranch: Equals/NotEquals/Contains/Matches
  2: [1, 2, 3, 4], // HeadBranch
  3: [1, 2, 3, 4], // Author
  4: [7, 8], // DraftStatus: IsTrue/IsFalse only
  5: [5, 6], // PrFilesChanged: GreaterThan/LessThan
  6: [5, 6], // PrLinesChanged
}

export const PATH_FILTER_MODE_LABELS: Record<PathFilterMode, string> = {
  1: 'Include',
  2: 'Exclude',
}

/** Mirrors App\Enums\RepoStack. Offered in the repo settings' Stack dropdown. */
export const REPO_STACK_LABELS: Record<RepoStack, string> = {
  1: 'Laravel API',
  2: 'Vue app',
}

export const RULE_SOURCE_LABELS: Record<RuleSource, string> = {
  1: 'Auto-generated',
  2: 'Manual',
  3: 'Imported',
}

export const RULE_LANGUAGE_LABELS: Record<RuleLanguage, string> = {
  1: 'TypeScript',
  2: 'JavaScript',
  3: 'Vue',
  4: 'PHP',
  5: 'Python',
  6: 'Go',
  7: 'Rust',
  8: 'Ruby',
  9: 'Java',
  10: 'Kotlin',
  11: 'Swift',
  12: 'C#',
  13: 'SQL',
  14: 'CSS',
  15: 'HTML',
  16: 'Shell',
}

export const RULE_LANGUAGE_OPTIONS = Object.entries(RULE_LANGUAGE_LABELS).map(([value, label]) => ({ value, label }))

export const RULE_STATUS_LABELS: Record<RuleStatus, string> = {
  1: 'Active',
  2: 'Disabled',
  3: 'Pending review',
}

export const RULE_STATUS = {
  ACTIVE: 1,
  DISABLED: 2,
  PENDING_REVIEW: 3,
} as const satisfies Record<string, RuleStatus>

export const REPO_RULE_DRAFT_STATUS = {
  PENDING: 1,
  COMPLETED: 2,
  FAILED: 3,
} as const satisfies Record<string, RepoRuleDraftStatus>

export const CLAUDE_MD_SUGGESTION_STATUS = {
  PENDING: 1,
  RUNNING: 2,
  READY: 3,
  FAILED: 4,
} as const satisfies Record<string, ClaudeMdSuggestionStatus>

export const LESSON_SOURCE_LABELS: Record<LessonSource, string> = {
  1: 'Self-check',
  2: 'Code review',
}

export const RULE_STATUS_BADGE_CLASSES: Record<RuleStatus, string> = {
  1: 'border-primary/40 text-primary',
  2: 'border-outline-variant/30 text-on-surface-variant',
  3: 'border-tertiary/40 text-tertiary',
}

export const REPO_SCAN_STATUS_LABELS: Record<RepoScanStatus, string> = {
  1: 'Queued',
  2: 'Running',
  3: 'Completed',
  4: 'Failed',
}

export const REPO_SCAN_STATUS = {
  QUEUED: 1,
  RUNNING: 2,
  COMPLETED: 3,
  FAILED: 4,
} as const satisfies Record<string, RepoScanStatus>

export const REPO_SCAN_STATUS_BADGE_CLASSES: Record<RepoScanStatus, string> = {
  1: 'border-outline-variant/30 text-on-surface-variant',
  2: 'border-tertiary/40 text-tertiary',
  3: 'border-primary/40 text-primary',
  4: 'border-error/40 text-error',
}

export const CODE_GRAPH_STATUS_LABELS: Record<CodeGraphStatus, string> = {
  1: 'Queued',
  2: 'Running',
  3: 'Completed',
  4: 'Failed',
}

export const CODE_GRAPH_STATUS = {
  QUEUED: 1,
  RUNNING: 2,
  COMPLETED: 3,
  FAILED: 4,
} as const satisfies Record<string, CodeGraphStatus>

export const CODE_GRAPH_STATUS_BADGE_CLASSES: Record<CodeGraphStatus, string> = {
  1: 'border-outline-variant/30 text-on-surface-variant',
  2: 'border-tertiary/40 text-tertiary',
  3: 'border-primary/40 text-primary',
  4: 'border-error/40 text-error',
}

export const REVIEW_RUN_STATUS_LABELS: Record<ReviewRunStatus, string> = {
  1: 'Queued',
  2: 'Running',
  3: 'Completed',
  4: 'Failed',
  5: 'Skipped',
  6: 'Cancelled',
  7: 'Deferred',
}

export const REVIEW_RUN_STATUS = {
  QUEUED: 1,
  RUNNING: 2,
  COMPLETED: 3,
  FAILED: 4,
  SKIPPED: 5,
  CANCELLED: 6,
  DEFERRED: 7,
} as const satisfies Record<string, ReviewRunStatus>

// Text color per status, shared by ReviewHistoryView's badge/dot and
// DashboardView's plain-text activity rows so the two never drift.
export const REVIEW_RUN_STATUS_TEXT_CLASSES: Record<ReviewRunStatus, string> = {
  1: 'text-on-surface-variant',
  2: 'text-primary-fixed',
  3: 'text-secondary-fixed',
  4: 'text-error',
  5: 'text-on-surface-variant',
  6: 'text-on-surface-variant',
  // Deliberately not the same muted grey as skipped/cancelled: those are
  // decisions, deferred is a review still waiting on something and worth
  // noticing when it stops moving.
  7: 'text-tertiary-fixed',
}

export const REVIEW_RUN_STATUS_BADGE_CLASSES: Record<ReviewRunStatus, string> = {
  1: `bg-surface-container-highest border-outline-variant/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[1]}`,
  2: `bg-primary-container/20 border-primary-container/30 animate-pulse ${REVIEW_RUN_STATUS_TEXT_CLASSES[2]}`,
  3: `bg-secondary-container/20 border-secondary-container/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[3]}`,
  4: `bg-error-container/20 border-error-container/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[4]}`,
  5: `bg-surface-container-highest border-outline-variant/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[5]}`,
  6: `bg-surface-container-highest border-outline-variant/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[6]}`,
  7: `bg-tertiary-container/20 border-tertiary-container/30 ${REVIEW_RUN_STATUS_TEXT_CLASSES[7]}`,
}

export const REVIEW_RUN_STATUS_DOT_CLASSES: Record<ReviewRunStatus, string> = {
  1: 'bg-outline-variant',
  2: 'bg-primary-fixed',
  3: 'bg-secondary-fixed shadow-[0_0_4px_currentColor]',
  4: 'bg-error',
  5: 'bg-outline-variant',
  6: 'bg-outline-variant',
  7: 'bg-tertiary-fixed',
}

// Single source of truth for both maps below, so the display label and the
// backend's snake_case value (AiUsageOperation::label(), e.g. "code_review",
// as seen in TicketBreakdown['operation_breakdown']) can't drift apart from
// each other — only from the backend enum itself, which nothing on this side
// can prevent.
const AI_USAGE_OPERATIONS: ReadonlyArray<{ id: AiUsageOperation; label: string; backendLabel: string }> = [
  { id: 1, label: 'Repo scan — mine rules', backendLabel: 'repo_scan_mine_rules' },
  { id: 2, label: 'Repo scan — dedupe rules', backendLabel: 'repo_scan_dedupe_rules' },
  { id: 3, label: 'Code review', backendLabel: 'code_review' },
  { id: 4, label: 'Repo rule — refine', backendLabel: 'repo_rule_refine' },
  { id: 5, label: 'Repo rule — improve', backendLabel: 'repo_rule_improve' },
  { id: 6, label: 'Finding reply — bot response', backendLabel: 'finding_reply' },
  { id: 7, label: 'Project build', backendLabel: 'project_build' },
  { id: 8, label: 'Skill pipeline step', backendLabel: 'skill_pipeline_step' },
  { id: 9, label: 'Repo rule — skill import', backendLabel: 'repo_rule_skill_import' },
  { id: 10, label: 'Repo rule — generate', backendLabel: 'repo_rule_generate' },
  { id: 11, label: 'Project plan', backendLabel: 'project_plan' },
  { id: 12, label: 'Auto-fix review comments', backendLabel: 'auto_fix_review_comments' },
  { id: 13, label: 'Code review — empty findings retry', backendLabel: 'code_review_empty_findings_retry' },
  { id: 14, label: 'Self-check', backendLabel: 'self_check' },
  { id: 15, label: 'Voice plan — draft', backendLabel: 'voice_plan_draft' },
  { id: 16, label: 'Code graph — generate', backendLabel: 'code_graph_generate' },
  { id: 17, label: 'CLAUDE.md suggestion — draft', backendLabel: 'claude_md_suggestion_draft' },
  { id: 18, label: 'Independent verification', backendLabel: 'independent_verification' },
  { id: 19, label: 'Jev', backendLabel: 'jev' },
  { id: 20, label: 'Code review — self-check', backendLabel: 'code_review_self_check' },
]

export const AI_USAGE_OPERATION_LABELS: Record<AiUsageOperation, string> = Object.fromEntries(
  AI_USAGE_OPERATIONS.map((operation) => [operation.id, operation.label]),
) as Record<AiUsageOperation, string>

export const AI_USAGE_OPERATION_BY_LABEL: Record<string, AiUsageOperation> = Object.fromEntries(
  AI_USAGE_OPERATIONS.map((operation) => [operation.backendLabel, operation.id]),
)

export function getAiOperationId(operationLabel: string): AiUsageOperation | undefined {
  const operation = AI_USAGE_OPERATION_BY_LABEL[operationLabel]
  if (operation === undefined) {
    console.warn(`Unknown AI operation label: ${operationLabel}`)
  }
  return operation
}
